/**
 * Multi-Agent Simulation — Type Definitions
 * ─────────────────────────────────────────
 * Shared across backend engine + frontend renderers.
 * See WTM-Multi-Agent-Simulation-Spec.md for the 5-stage pipeline.
 */

// ─── Tier config ────────────────────────────────────────────────────────────

export type SimulationTier = "quick_scan" | "standard" | "deep_dive";

export interface TierConfig {
  id: SimulationTier;
  label: string;
  agents: number;
  rounds: number;
  estimatedTime: string;
  description: string;
}

export const SIMULATION_TIERS: Record<SimulationTier, TierConfig> = {
  quick_scan: {
    id: "quick_scan",
    label: "Quick Scan",
    agents: 20,
    rounds: 10,
    estimatedTime: "~2-3 min",
    description: "Fast sentiment check — one investor per archetype",
  },
  standard: {
    id: "standard",
    label: "Standard",
    agents: 40,
    rounds: 15,
    estimatedTime: "~4-6 min",
    description: "Full debate with position shifts — two per archetype",
  },
  deep_dive: {
    id: "deep_dive",
    label: "Deep Dive",
    agents: 80,
    rounds: 20,
    estimatedTime: "~8-12 min",
    description: "Thorough multi-cycle analysis — four per archetype",
  },
};

// ─── The 20 archetypes ──────────────────────────────────────────────────────

export type ArchetypeCategory = "A" | "B" | "C" | "D";

export interface Archetype {
  name: string;
  category: ArchetypeCategory;
  description: string;
}

export const ARCHETYPES: Archetype[] = [
  // Category A — Fundamental (the "why" investors)
  { name: "Deep Value Investor", category: "A", description: "Balance sheet first. Margin of safety obsessed. Buys what everyone hates." },
  { name: "Growth Investor", category: "A", description: "TAM, revenue growth, market penetration. Pays high multiples for long runways." },
  { name: "GARP Investor", category: "A", description: "Growth at Reasonable Price. PEG ratio bible. Value/growth middle ground." },
  { name: "Quality Compounder Hunter", category: "A", description: "ROCE, capital allocation, management quality. Holds for 20 years." },
  { name: "Dividend Income Investor", category: "A", description: "Yield focused. Predictable cash flows, high payout ratios, low volatility." },
  { name: "Turnaround Specialist", category: "A", description: "Broken companies with fixable problems. High risk/reward." },
  // Category B — Market/Technical (the "when" investors)
  { name: "Momentum Trader", category: "B", description: "Price action only. 50/200 DMA, relative strength, breakouts." },
  { name: "Swing Trader", category: "B", description: "Days-to-weeks catalyst plays, earnings setups, technical patterns." },
  { name: "Quant Systematic Investor", category: "B", description: "Factor-based: value, momentum, quality, volatility. Distrusts narratives." },
  { name: "Macro Strategist", category: "B", description: "Top-down: rates, currency, sector rotation, global flows." },
  // Category C — Skeptics & Contrarians (the "what could go wrong" investors)
  { name: "Short Seller Bear", category: "C", description: "Overvaluation, accounting red flags, governance issues." },
  { name: "Contrarian", category: "C", description: "Argues opposite of consensus. Skeptical of crowded trades." },
  { name: "Risk Manager", category: "C", description: "Downside scenarios, tail risks, correlation, concentration." },
  { name: "Forensic Accountant", category: "C", description: "Reads footnotes. Cash flow vs earnings discrepancies, related party txns." },
  // Category D — Contextual/Specialty (the "different angle" investors)
  { name: "Retail Investor", category: "D", description: "Average individual. Social media influenced, brand-driven, represents flow." },
  { name: "Institutional Sell-Side Analyst", category: "D", description: "Sector professional. Consensus numbers, guidance, peer comps." },
  { name: "Venture Disruption Thinker", category: "D", description: "'What kills this in 10 years?' Tech shifts, new entrants, platforms." },
  { name: "ESG Sustainability Investor", category: "D", description: "Governance, environmental risk, social impact, regulatory exposure." },
  { name: "Insider Promoter Watcher", category: "D", description: "Insider buying/selling, pledged shares, capital allocation track record." },
  { name: "Index Passive Rebalancer", category: "D", description: "Weight changes, inclusion/exclusion risk, float adjustments." },
];

// ─── Name pools for localization ────────────────────────────────────────────

export const INDIAN_NAME_POOL = [
  "Deepak","Kavitha","Rohit","Priya","Arjun","Meera","Sanjay","Anita","Vikram","Neha",
  "Rajesh","Sunita","Aditya","Pooja","Harsh","Divya","Nikhil","Swati","Kiran","Manish",
  "Lakshmi","Gautam","Ritu","Pranav","Ishita","Amit","Shreya","Vishal","Nandini","Kunal",
  "Pallavi","Suresh","Jaya","Raghav","Tanvi","Mohit","Bhavna","Aniket","Sneha","Siddharth",
  "Aparna","Varun","Megha","Tushar","Chitra","Himanshu","Rekha","Pankaj","Madhuri","Gaurav",
  "Usha","Tarun","Preeti","Ashok","Seema","Ramesh","Sakshi",
];

export const US_NAME_POOL = [
  "James","Sarah","Mike","Emily","David","Rachel","Chris","Amanda","Ryan","Jessica",
  "Marcus","Jennifer","Alex","Stephanie","Kevin","Lauren","Brian","Nicole","Tyler","Megan",
  "Jordan","Ashley","Daniel","Heather","Nathan","Rebecca","Jake","Lisa","Connor","Samantha",
  "Greg","Michelle","Brandon","Tiffany","Derek","Courtney","Kyle","Andrea","Ethan","Chelsea",
  "Sean","Diana","Trevor","Monica","Scott","Vanessa","Cameron","Carolyn","Blake","Angela",
];

// ─── Seed material ──────────────────────────────────────────────────────────

export interface SeedMaterial {
  ticker: string;
  stockName: string;
  exchange: string;
  currency: string;
  profile: {
    sector: string;
    industry: string;
    marketCap: number;
    description?: string;
  };
  currentPrice: number;
  priceChangePct: number;
  financials: Array<{
    period: string;
    type: "ANNUAL" | "QUARTERLY";
    revenue: number;
    netProfit: number;
    eps: number;
    roe: number;
    roce: number;
    debtToEquity: number;
    freeCashFlow: number;
  }>;
  keyRatios: {
    pe?: number;
    pb?: number;
    roe?: number;
    debtToEquity?: number;
    currentRatio?: number;
  };
  recentNews: Array<{ headline: string; summary: string; date: string; impact: string }>;
  existingReportSummary?: string;
  userThesis?: string;
}

// ─── Knowledge graph ────────────────────────────────────────────────────────

export interface Entity {
  name: string;
  type: "company" | "person" | "product" | "market" | "event" | "other";
  role: string;
}

export interface Relationship {
  from: string;
  to: string;
  type: string;
  detail: string;
}

export interface KnowledgeGraph {
  entities: Entity[];
  relationships: Relationship[];
  keyMetrics: Record<string, number | string>;
  catalysts: string[];
  risks: string[];
  sectorContext: string;
}

// ─── Agents ─────────────────────────────────────────────────────────────────

export type Stance = "bullish" | "bearish" | "neutral";

export interface SimulationAgent {
  id: string;
  name: string;
  archetype: string;
  archetypeCategory: ArchetypeCategory;
  age: number;
  background: string;
  investmentStyle: string;
  initialStance: Stance;
  currentStance: Stance;
  initialReasoning: string;
  keyConcern: string;
  personality: string;
  participationRate: number; // 0-1
  stubbornness: number;      // 0-1
}

// ─── Rounds ─────────────────────────────────────────────────────────────────

export type AgentAction = "new_argument" | "reply" | "position_change" | "silent";

export interface AgentPost {
  agentId: string;
  agentName: string;
  archetype: string;
  action: AgentAction;
  stance: Stance;
  content: string;
  replyToAgentId?: string;
  replyToAgentName?: string;
  previousStance?: Stance;
  newStance?: Stance;
}

export interface SimulationRound {
  round: number;
  posts: AgentPost[];
  sentiment: { bullish: number; bearish: number; neutral: number };
  positionChanges: number;
}

// ─── Engine events (SSE) ────────────────────────────────────────────────────

export type OrchestrationStage =
  | "pending"
  | "seed_assembly"
  | "graph_building"
  | "agent_generation"
  | "simulating"
  | "synthesizing"
  | "completed"
  | "failed";

export type SimulationEvent =
  | { type: "stage"; stage: OrchestrationStage; message: string }
  | { type: "metadata"; stockName: string; ticker: string; exchange: string; tier: SimulationTier; totalAgents: number; totalRounds: number }
  | { type: "agents_ready"; agents: SimulationAgent[] }
  | { type: "round_start"; round: number; totalRounds: number }
  | {
      type: "agent_post";
      round: number;
      agentId: string;
      agentName: string;
      archetype: string;
      stance: Stance;
      action: AgentAction;
      content: string;
      replyToAgentName?: string;
      previousStance?: Stance;
      newStance?: Stance;
    }
  | {
      type: "round_end";
      round: number;
      sentiment: { bullish: number; bearish: number; neutral: number };
      positionChanges: number;
    }
  | { type: "early_termination"; round: number; reason: string }
  | { type: "simulation_complete"; finalSentiment: { bullish: number; bearish: number; neutral: number } }
  | { type: "complete"; simulationId: string; slug: string }
  | { type: "error"; message: string };

// ─── Report ─────────────────────────────────────────────────────────────────

export type ConsensusDirection = "strong_sell" | "sell" | "neutral" | "buy" | "strong_buy";

export interface VerdictSection {
  consensusDirection: ConsensusDirection;
  consensusPercentage: number;
  confidenceScore: number; // 0-100
  confidenceLabel: "Low" | "Moderate" | "High" | "Very High" | "Extreme";
  summaryParagraph: string;
}

export interface InvestorPanelEntry {
  archetype: string;
  agentCount: number;
  bullishCount: number;
  bearishCount: number;
  neutralCount: number;
  verdictLabel: "Bullish" | "Bearish" | "Neutral" | "Mixed";
  verdictPercentage: number;
  keyArgument: string;
  notableQuotes: string[];
}

export interface DebateHighlightExchange {
  agentName: string;
  archetype: string;
  stance: Stance;
  content: string;
}

export interface DebateHighlight {
  roundNumber: number;
  title: string;
  description: string;
  exchanges: DebateHighlightExchange[];
  impact: string;
}

/**
 * Where a scenario band came from. "model" is derived from this debate; "fallback" is
 * the synthesizer's neutral placeholder, emitted when the model returned no usable
 * scenario set. Optional because reports written before RM-W1-08 carry neither value,
 * and those legacy reports get blanket wording in the viewer instead.
 */
export type ScenarioProvenance = "model" | "fallback";

export interface ScenarioEntry {
  scenario: "bull" | "base" | "bear";
  probability: number;
  title: string;
  description: string;
  fairValueRange: { low: number; high: number; currency: string };
  keyDrivers: string[];
  keyRisks: string[];
  timeframe: string;
  provenance?: ScenarioProvenance;
}

export interface WatchItem {
  catalyst: string;
  expectedDate: string;
  potentialImpact: "high" | "medium" | "low";
  description: string;
}

export interface SimulationReport {
  verdict: VerdictSection;
  investorPanel: InvestorPanelEntry[];
  debateHighlights: DebateHighlight[];
  scenarioAnalysis: ScenarioEntry[];
  watchList: WatchItem[];
  disclaimer: string;
}

export const SIMULATION_DISCLAIMER =
  "This simulation is an experimental AI feature. The investor personas are AI-generated and do not represent real individuals or their views. The consensus, scenarios, and analysis presented are the output of a language model simulation and should not be interpreted as investment advice, recommendations, or predictions. Always conduct your own research and consult a qualified financial advisor before making investment decisions. Past simulation results do not indicate future accuracy. WhatsTheMoat is not a registered investment advisor.";

// ─── Cost cap ───────────────────────────────────────────────────────────────

/** Hard cap per simulation: aborts with error if exceeded. The run is marked failed and the
 * credit IS refunded (orchestrator catch, refundSimulationCredit). */
export const SIMULATION_COST_HARD_CAP_USD = 1.5;
